import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, Inject, InjectionToken, OnInit, Optional, Renderer2, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationStart, ParamMap, Router } from '@angular/router';
import { BadgeComponent, ButtonDirective } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { AuthService } from 'auth';
import { CalendarEvent, EventDetailComponent } from 'calendar';
import { I18nService, TranslatePipe } from 'i18n';
import { InvitationService } from 'invitation';
import { Media, MediaDetailComponent, MediaEntity, MediaExplorerComponent, MediaGridComponent, MediaService, MediaViewerOverlayService, NULL_MEDIA } from 'media';
import { asyncScheduler, combineLatest, filter, from, lastValueFrom, map, Observable, of, ReplaySubject, Subscription, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { Action, AdaptiveButtonComponent, contrastColor, HasId, Logger, Platform } from 'shared';

export const MEDIA_PAGE_DATA_SOURCE = new InjectionToken<MediaPageDataSource<HasId>>('MEDIA_PAGE_DATA_SOURCE');

export interface MediaPageDataSource<T extends HasId> {
  load(path: string): Observable<T[]>;
  mediaOf(entity: T | string): Observable<Media | null>;
  typeOf(entity: T): string;
  update(entity: Partial<T>): Observable<T>;
  delete(entityId: string): Observable<void>;
}

export function mediaDataSourceFactory(
  mediaService: MediaService,
  i18n: I18nService,
  route: ActivatedRoute
): MediaPageDataSource<Media> {
  const area = route.snapshot.data['area'];
  return {
    load: (path: string) =>
      i18n.currentLanguage$.pipe(
        switchMap(lang =>
          mediaService.loadMedia(lang, area, path)
        )
      ),
    mediaOf: (media: Media | string) => typeof media === 'string' ? mediaService.getMediaById(media) : of(media),
    typeOf: (media: Media) => 'media',
    update: (entity: Media) => of(entity),
    delete: (entityId: string) => of()
  };
}

export interface MediaPageConfig {
  createNullEntity: () => MediaEntity;
  action: Action;
}
export const MEDIA_PAGE_CONFIG = new InjectionToken<MediaPageConfig>('MEDIA_PAGE_CONFIG');
export const PATH_FILTER_PREFIX = 'path:';

@Component({
  selector: 'media-page',
  templateUrl: './media-page.component.html',
  styleUrls: ['./media-page.component.scss'],
  imports: [MediaGridComponent, MediaExplorerComponent, CommonModule, TranslatePipe, AdaptiveButtonComponent, EventDetailComponent, MediaDetailComponent, BadgeComponent, IconModule, ButtonDirective],
  animations: [
    trigger('slideInOut', [
      state('in', style({ transform: 'translateX(0)' })),
      state('out', style({ transform: 'translateX(100%)' })),
      transition('out => in', animate('300ms ease-in-out')),
      transition('in => out', animate('300ms ease-in-out'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MediaPageComponent implements OnInit {
  entities = signal<MediaEntity[]>([]);
  mediaOf  = (entity: MediaEntity) => this.dataSource.mediaOf(entity);
  typeOf   = (entity: MediaEntity) => this.dataSource.typeOf(entity);
  createNullEntity = this.config?.createNullEntity || (() => (this.nullMedia));
  selectedEntity = signal<any>(this.createNullEntity());
  selectedEntityEditMode = signal<boolean>(false);
  // The media currently browsed as a file explorer. Set from the route, so a
  // deep link and the browser's back button work like they do for folders.
  explorerMedia = signal<Media | null>(null);
  // toSignal(
  //   combineLatest([
  //     toObservable(this.selectedEntity),
  //     this.authService.getUser$(),
  //   ]).pipe(
  //     map(([entity, user]) => {
  //       this.logger.debug('selectedEntityEditMode', { entity, user });
  //       return (entity?.id === this.createNullEntity()?.id || entity?.authorId == user?.id)
  //     })
  //   ),
  //   { initialValue: false }
  // );
  

  protected viewPath: string   = '';
  protected viewStack: Media[] = [null as unknown as Media];
  protected viewIds: string[]  = [];
  protected viewed$        = new ReplaySubject<Media | null>(1);
  protected parent$        = new ReplaySubject<Media | null>(1);
  protected initialized$   = new ReplaySubject<boolean>(1);
  protected area!: string;
  protected mediaViewerSubscription: Subscription | undefined;
  protected mediaSubscription: Subscription | undefined;

  protected browserSubscription: Subscription | null = null;

  offcanvas = viewChild<ElementRef>('offcanvas');
  offcanvasContent = viewChild('offcanvasContent',{ read: ElementRef });
  protected displaySidebar = signal<boolean>(false);
  protected removeListener: () => void = () => {};
  
  contrastColor = contrastColor;

  sampleEvent: CalendarEvent = {
    id: 'evt-001',
    type: 'conference',
    name: 'Team Sync Meeting',
    description: 'Weekly team meeting to review progress and upcoming tasks.',
    timeFrom: 1733475600000, // Example timestamp (2024-12-06 10:00 UTC)
    timeTo: 1733479200000,   // Example timestamp (2024-12-06 11:00 UTC)
    media: {
      id: '0',
      name: 'Team Sync Meeting',
      type: 'image',
      cover: {
          src: 'https://img.freepik.com/premium-vector/colleagues-sitting-table-coffee-break_662525-949.jpg',
          mimeType: 'image/jpg',
          width: 0,
          height: 0,
          size: 0
        }
    },
    location: 'Conference Room A',
    timezoneId: 'Europe/Sofia',
    tags: ['meeting', 'team', 'weekly'],
    color: '#4A90E2',
    language: this.i18nService.getLanguage()
  }
  protected logger = new Logger('MediaPageComponent');
  
  constructor(
    private i18nService: I18nService, 
    private mediaService: MediaService,
    private mediaViewerOverlayService: MediaViewerOverlayService,
    private authService: AuthService,
    private invitationService: InvitationService,
    private platform: Platform,
    private inAppBrowser: InAppBrowser,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private destroyRef: DestroyRef,
    private renderer: Renderer2,
    @Inject(NULL_MEDIA) private nullMedia: Media,
    @Optional() @Inject(MEDIA_PAGE_DATA_SOURCE) protected dataSource: MediaPageDataSource<MediaEntity>,
    @Optional() @Inject(MEDIA_PAGE_CONFIG) public config?: MediaPageConfig
  ) {
    this.dataSource = dataSource ?? mediaDataSourceFactory(mediaService, i18nService, route);
    effect(() => {
      const entity = this.selectedEntity();
      const element = this.offcanvasContent()?.nativeElement;
      if (entity && element) {
        queueMicrotask(() => {
          element.scrollTop = 0;
        });
      }
    });

    combineLatest([
      toObservable(this.selectedEntity),
      this.authService.getUser$(),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([entity, user]) => {
        this.logger.debug('selectedEntityEditMode', { entity, user });
        return (entity?.id === this.createNullEntity()?.id || entity?.authorId == user?.id)
      })
    ).subscribe((editMode) => {
      this.selectedEntityEditMode.set(editMode);
    })
  }

  ngOnInit() {
    this.area = this.route.snapshot.data['area'] || 'welcome';
    this.viewed$.next(null);
    this.route.paramMap.pipe(
      map((params: ParamMap) => params.get('path')),
      takeUntilDestroyed(this.destroyRef)
    ).pipe(
      map(path => {
        const parts = path?.split('/').filter(part => !!part) || [];
        let entityId: string | null = null;
        let viewPath: string | null = null;
        if (parts?.length > 0) {
          if (parts[0]?.startsWith(PATH_FILTER_PREFIX)) {
            viewPath = parts[0].substring(PATH_FILTER_PREFIX.length);
          }
          if (parts.length > 1 || !viewPath) {
            entityId = parts[parts.length - 1];
          }
        }
        return [path, viewPath, entityId];
      }),
    ).subscribe(([path, viewPath, entityId]) => {
      // if (path?.length) {
      //   const tree: UrlTree = this.router.parseUrl(path);
      //   const group: UrlSegmentGroup = tree.root.children[this.route.outlet] || tree.root;
      //   const segments: UrlSegment[] = group.segments || [];
      //   const entityId = segments.length ? segments[segments.length - 1].path : null;
      //   const mediaParams = segments.length ? segments[segments.length - 1].parameters : null;
      //   this.logger.debug('path', { path, entityId, mediaParams });
      // }
      this.initialized$.next(false);
      this.explorerMedia.set(null);
      this.viewPath = viewPath || '';
      this.viewIds  = viewPath?.split('.') || []; //.filter(entityId => !!entityId) || [];

      // this.viewStack.splice(1);

       // TODO: enable this code when we have separate server api for loading media by id
      // load all media ids in the view path
      // const promise: Promise<Media | null> = this.viewIds.length > 0
      // ? this.viewIds.reduce((promise, entityId, index) => {
      //     promise = promise.then(() => {
      //       return lastValueFrom(this.mediaService.getMediaById(entityId))
      //       .then((media: Media | null) => {
      //         if (media) {
      //           this.viewStack.push(media)
      //         }
      //         return media;
      //       });
      //     });
      //     return promise;
      //   }, Promise.resolve(null) as Promise<Media | null>)
      // : Promise.resolve(null);

      const promise = lastValueFrom(
        this.loadMedia(this.viewPath).pipe(
          take(1),
          switchMap(() => entityId ? this.dataSource.mediaOf(entityId) : of(null))
        )
      );
      promise.then((media: Media | null) => {
        // this.viewed$.next(this.viewStack[this.viewStack.length - 1]);
        // this.parent$.next(this.viewStack?.length > 1 ? this.viewStack[this.viewStack.length - 2] : null);
        if (!media || Media.isFolder(media)) {
          this.mediaSubscription?.unsubscribe();
          this.mediaSubscription = this.loadMedia(this.viewPath).subscribe();
        } else if (Media.isExplorer(media)) {
          // Rendered in place of the grid, inside the page - not in the media
          // viewer overlay, which covers the whole screen including the way back.
          this.explorerMedia.set(media);
        } else {
          if (entityId) {
            const url = this.router.url || '';
            this.openMediaViewer(media, url?.substring(0, url.lastIndexOf(`/${entityId}`)));
            // TODO: this.viewPath is not pointing to the parent folder of the media
            // take the parent folder of the media and load it
            // this.mediaService
            //   .getMediaById(entityId)
            //   .pipe(filter(media => !!media), take(1))
            //   .subscribe((media: Media) => {
            //     const url = this.router.url || '';
            //     this.openMediaViewer(media, url?.substring(0, url.lastIndexOf(`/${entityId}`)));
            //   });
          }
        }
        this.initialized$.next(true);
      });
    });

    //reload when curren user accepts/declines relationship request 
    // (e.g. opens invitation link)
    this.invitationService
        .relationship
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.loadMedia(this.viewPath);
        });

    this.removeListener = this.renderer.listen(
      document,
      'click',
      this.onDocumentClick.bind(this),
      { capture: true }
    );
  }

  ngOnDestroy() {
    this.mediaSubscription?.unsubscribe();
    this.removeListener();
  }

  async onSelect(entity: MediaEntity) {
    try {
      const media = await lastValueFrom(this.mediaOf(entity));
      if (media) {
        if (Media.isFolder(media)) {
          this.openMediaFolder(media);
        }
        else if (Media.isExplorer(media)) {
          this.openMediaExplorer(media);
        }
        // else if (Media.isConference(media)) {
        //   this.joinConference(media);
        // } 
        else if (Media.isLink(media)) {
          this.openLink(media);
        } else if (media) {
          this.openMediaViewer(media);
        }
      } else {
        this.logger.warn('Missing entity media', entity);
      }
    } catch (error) {
      this.logger.warn('Failed to get media for entity', { entity, error });
    }
    
  }

  onDetails(entity: HasId | null) {
    this.logger.log('ENTITY');
    this.selectedEntity.set(entity);
    this.displaySidebar.set(true);
  }

  onSave(entity: Partial<MediaEntity>) {
    this.dataSource.update(entity).pipe(
      tap(result => {
        this.logger.info('onSave - completed', { entity, result });
        this.selectedEntity.set(result);
        asyncScheduler.schedule(() => 
           // override the effect of selectedEntityEditMode.set(true) invokation
           // performed by the reactive code (defined in the ctor of this component)
           // which was triggered by the selectedEntity.set() call above
          this.selectedEntityEditMode.set(false)
        , 10) 
        // this.closeSidebar();
      }),
      switchMap(() => this.loadMedia(this.viewPath).pipe(take(1)))
    ).subscribe();
  }

  onDelete(entityId: string) {
    this.dataSource.delete(entityId).subscribe(() => {
      this.logger.info('onDelete - completed', entityId);
      this.closeSidebar();
      this.loadMedia(this.viewPath)
          .pipe(take(1))
          .subscribe();
    })
  }

  onCancel(entity: unknown) {
    this.closeSidebar();
  }

  onSlideInOutAnimationStart(event: AnimationEvent) {
    if (event.toState == 'in') {
      const offcanvas = this.offcanvas();
      this.renderer.setStyle(offcanvas?.nativeElement, 'display', 'flex');
    }
  }
  onSlideInOutAnimationDone(event: AnimationEvent) {
    if (event.toState == 'out') {
      // first set the content (i.e. event detail) scroll position to 0 and then hide the offcanvas
      // if we reverse the order of these two action the browser will restore the scroll position 
      // of the content when the offcanvas is displayed again
      const element = this.offcanvasContent()?.nativeElement;
      if (element) {
        element.scrollTop = 0;
      }
      const offcanvas = this.offcanvas();
      this.renderer.setStyle(offcanvas?.nativeElement, 'display', 'none');
    }
  }

  onDocumentClick(event: MouseEvent): void {
    if (this.displaySidebar()) {
      const target = event.target as HTMLElement;
      const offcanvasElement = this.offcanvas()?.nativeElement; //document.querySelector('.media-offcanvas');
      const insideOverlay = !!target.closest('.cdk-overlay-container, .ngx-colors-overlay');
      if (!offcanvasElement?.contains(target) && !insideOverlay) {
        this.closeSidebar();
      }
    }
  }

  getBadgeInfo(media: Media) {
    return this.mediaService.getBadgeInfo(media);
  }

  protected closeSidebar(): void {
    this.selectedEntity.set(undefined);
    this.displaySidebar.set(false);
  }

  protected openMediaFolder(media: Media): Promise<void> {
    this.logger.log("OPEN MEDIA FOLDER");
    return lastValueFrom(
      this.initialized$.pipe(
        filter(initialized => !!initialized),
        take(1),
        switchMap(() => {
          const viewedMediaId = this.viewIds[this.viewIds.length - 1];
          // const viewedMedia = this.viewStack[this.viewStack.length - 1];
          this.logger.debug('openMediaFolder', { media, viewedMediaId });
          if (media.id !== viewedMediaId) {
            let path = this.location.path();
            path = path?.split(/[?#]/)[0] || ''; // Remove query string and hash
            const trunk = path.split(`/${PATH_FILTER_PREFIX}`)[0] || '';
            // const isParent = (media: Media, parent: Media) => parent == null ? true : media?.parentIds?.includes(parent.id);
            // if (!viewedMedia || isParent(media, viewedMedia)) {
            //   this.viewStack.push(media); // Open
            // } else if (!media || isParent(viewedMedia, media)) {
            //   this.viewStack.pop(); // Back
            // }
            // this.viewPath = this.viewStack.length > 1 ? this.viewStack.slice(1).map(media => media.id).join('.') : '';
            this.viewIds.push(media.id);
            this.viewPath = this.viewIds.join('.');
            if (this.viewPath.length) {
              path = `${trunk}/${PATH_FILTER_PREFIX}${encodeURIComponent(this.viewPath)}`;
            }
            this.logger.debug('openMediaFolder -> navigateByUrl', path);
            return from(this.router.navigateByUrl(path).then(() => {
              this.viewed$.next(media);
              // this.parent$.next(this.viewStack.length > 1 ? this.viewStack[this.viewStack.length - 2] : null);
            }));
          }
          return of(void 0);
        })
      )
    );
  }

  /**
   * Opens an explorer item as a real router navigation (appending its id to the
   * current path, the same shape the viewer deep link uses). A router navigation
   * rather than location.go(), so that browser back re-emits the route params
   * and the page falls back to the grid on its own.
   */
  protected openMediaExplorer(media: Media): Promise<boolean> {
    const path = this.location.path().split(/[?#]/)[0] || '';
    return this.router.navigateByUrl(`${path}/${encodeURIComponent(media.id)}`);
  }

  /** Leaves the explorer - back to the listing it was opened from. */
  protected closeMediaExplorer(): void {
    const media = this.explorerMedia();
    const path = this.location.path().split(/[?#]/)[0] || '';
    const suffix = `/${encodeURIComponent(media?.id ?? '')}`;
    const parent = media && path.endsWith(suffix) ? path.slice(0, -suffix.length) : path;
    this.router.navigateByUrl(parent);
  }

  protected openMediaViewer(media: Media, path = this.location.path()) {
    this.mediaViewerSubscription?.unsubscribe();
    this.mediaViewerOverlayService
        .open({ media })
        .then(overlayRef => {
          this.mediaViewerSubscription = new Subscription();
          const mediaPath = `${path}/${encodeURIComponent(media.id)}`;
          let preventBackNavigation = false;
          const onOpen = () => {
            this.location.go(mediaPath); // this.location.go(`${this.location.path()}/${media.id}`);
          }

          const onClose = () => {
            if (!preventBackNavigation && this.location.isCurrentPathEqualTo(mediaPath)) {
              // location.back() does not work as expected on ios safari browser 
              // when navigating back from pdf viewer
              this.location.go(path); // this.location.back();
            }
          };

          this.mediaViewerSubscription.add(
            this.router.events
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((event) => {
                if (event instanceof NavigationStart) {
                  preventBackNavigation = true;
                  if (media.type!='conference') { // keep minimized conferences in the dom event on route change
                    overlayRef.destroy();
                  }
                }
              })
          );

          this.mediaViewerSubscription.add(
            overlayRef.onOpened
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                onOpen();
              })
          );

          this.mediaViewerSubscription.add(
            overlayRef.onClosed
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                onClose();
              })
          );

          this.mediaViewerSubscription.add(
            overlayRef.onDestroy
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe(() => {
                this.mediaViewerSubscription?.unsubscribe();
                this.mediaViewerSubscription = undefined;
                onClose();
              })
          );

          this.mediaViewerSubscription.add(
            overlayRef.onAction
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe(({action, parameter}) => {
                if (action=='played') {
                  const media = parameter as Media;
                  if (media.trackReactions) {
                    this.mediaService.setReaction((parameter as Media).id, 'played')
                    .then(() => {
                      this.loadMedia(this.viewPath)
                          .pipe(take(1))
                          .subscribe();
                    });
                  }
                }
              })
          );
        });
  }

  // protected navigateBack(): Promise<void> {
  //   if (this.viewStack.length > 1) {
  //     return this.openMediaFolder(this.viewStack[this.viewStack.length - 2]);
  //   }
  //   return Promise.reject('Failed to navigate back from root media');
  // }

  protected loadMedia(path: string): Observable<unknown[]> {
    return this.i18nService.currentLanguage$.pipe(
      switchMap((language) => 
        this.dataSource.load(path)
        // this.mediaService
        //   .loadMedia(language, this.area, path)
          .pipe(
              withLatestFrom(this.viewed$),
              map(([result, viewed]) => {
                this.logger.debug(`Received ${result.length} media entities`);
                if (viewed) {
                  result = result.map(media => {
                    // media.parentIds = [...(media.parentIds || []), viewed.id];
                    return media;
                  })
                }
                return result;
              }), 
              tap(result => this.entities.set(result))
          )
        )
    );
  }

  // joinConference(conference: Conference) {
  //   this.logger.debug('joinConference -> start', conference);
  //   this.router.navigate([`/${this.i18nService.getLanguage()}/conference`], { state: { data: conference } }).then(() => {
  //     this.logger.debug('joinConference -> completed', conference);
  //   });
  // }

  openLink(media: Media) {
    if (media && media.type === 'link') {
      const urlPattern = /^https?:\/\/[^\s$.?#].[^\s]*$/;
      if (urlPattern.test(media.url)) {
        if (this.platform.is('hybrid')) {
           // _system: opens in Safari,
           // _blank: opens in in-app browser window
          this.browserSubscription?.unsubscribe();
          const browser = this.inAppBrowser.create(media.url, '_system');
          this.browserSubscription = browser.on('exit')
            .pipe(
              take(1), 
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe()
          this.browserSubscription.add(() => browser.close());
        } else {
          window.open(media.url, '_blank', 'noopener,noreferrer');
        }
      } else {
        this.logger.error('Invalid URL', media.url);
      }
    }
  }
}